/*
 * "ERP": tarjetas para cargar el Excel nacional de cada metrica (visibles solo con igi_load_erp) y,
 * debajo, la comparativa % ERP vs IGI agrupada por zona (visible con igi_view_all/igi_view_own_territory).
 * Mismo patron pedido/cargando que el resto de igi.
 */
import React, {useCallback, useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import Breadcrumbs from "../../../../components/breadcrumbs";
import {formatoNota, nivel3} from "../niveles";
import {
    cargar_erp,
    clear_igi,
    get_erp_comparativa,
    get_igi_resumen,
    CARGAR_ERP_SUCCESS,
} from "../../../../redux/igi";

const METRICAS = [
    ["contratacion_oro", "Contratación Oro"],
    ["retorno", "Retorno"],
    ["retroventa", "Retroventa"],
    ["ventas_minoristas", "Ventas Oro Minoristas"],
    ["rosett", "Ejecutado Rosett"],
    ["univendidos", "Univendidos"],
];

const NIVEL_COLOR = {
    fuerte: {bg: "#e8f5e9", color: "#1a5e20"},
    media: {bg: "#fff8e1", color: "#7c4a00"},
    debil: {bg: "#ffebee", color: "#8b0000"},
};

const mesHoy = () => new Date().toISOString().slice(0, 7);

const TarjetaCarga = ({metrica, etiqueta, puedeCargar, token}) => {
    const dispatch = useDispatch();
    const resultado = useSelector((state) => state.igi_module._cargar_erp)
        || {data: {}, status: 0, message: {}};
    const [mes, setMes] = useState(mesHoy());
    const [archivo, setArchivo] = useState(null);
    const [pedido, setPedido] = useState(false);
    const [ultimoResultado, setUltimoResultado] = useState(null);

    const subir = () => {
        if (!archivo) return;
        setPedido(true);
        dispatch(clear_igi(CARGAR_ERP_SUCCESS));
        dispatch(cargar_erp(token, {archivo, metrica, mes}));
    };

    useEffect(() => {
        if (pedido && resultado.status !== 0 && resultado.metrica === metrica) {
            setPedido(false);
            setUltimoResultado(resultado);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resultado.status, resultado.metrica]);

    if (!puedeCargar) return null;

    return (
        <div className="card mb-1">
            <div className="card-body">
                <div style={{fontSize: "13px", fontWeight: 500}} className="mb-1">{etiqueta}</div>
                <div className="d-flex flex-wrap align-items-center" style={{gap: "0.5rem"}}>
                    <input
                        type="month"
                        className="form-control form-control-sm"
                        style={{maxWidth: "160px"}}
                        value={mes}
                        onChange={(e) => setMes(e.target.value)}
                    />
                    <input
                        type="file"
                        accept=".xlsx"
                        className="form-control form-control-sm"
                        style={{maxWidth: "220px"}}
                        onChange={(e) => setArchivo(e.target.files[0] || null)}
                    />
                    <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={!archivo || pedido}
                        onClick={subir}
                    >
                        {pedido ? "Subiendo…" : "Subir Excel"}
                    </button>
                </div>
                {ultimoResultado && ultimoResultado.status === 201 && (
                    <div className="text-muted mt-1" style={{fontSize: "12px"}}>
                        {ultimoResultado.data.cantidad} tienda(s) cargada(s)
                        {ultimoResultado.data.no_encontrados.length > 0
                            ? ` · sin encontrar: ${ultimoResultado.data.no_encontrados.join(", ")}` : ""}
                    </div>
                )}
                {ultimoResultado && ultimoResultado.status !== 0 && ultimoResultado.status !== 201 && (
                    <div className="text-danger mt-1" style={{fontSize: "12px"}}>
                        {ultimoResultado.message || "No se pudo cargar el archivo"}
                    </div>
                )}
            </div>
        </div>
    );
};

const ERP = () => {
    const dispatch = useDispatch();
    const {token} = useSelector((state) => state.users_module.login.data) || null;
    const permisos = useSelector((state) => state.users_module._session_data.data)?.grupo_permiso?.permisos || [];
    const puedeCargar = permisos.some((p) => p.codigo === "igi_load_erp");
    const resumen = useSelector((state) => state.igi_module._get_resumen);
    const comparativa = useSelector((state) => state.igi_module._get_erp_comparativa)
        || {data: {}, status: 0, message: {}};
    const resultado = useSelector((state) => state.igi_module._cargar_erp)
        || {data: {}, status: 0, message: {}};

    const [metrica, setMetrica] = useState(METRICAS[0][0]);
    const [mes, setMes] = useState(mesHoy());
    const [zona, setZona] = useState("");
    const [pedido, setPedido] = useState(false);

    const zonas = resumen.data && resumen.data.datos ? resumen.data.datos : [];

    useEffect(() => {
        dispatch(get_igi_resumen(token));
    }, [dispatch, token]);

    const claveActual = `${metrica}|${mes}|${zona}`;

    const recargar = useCallback(() => {
        setPedido(true);
        dispatch(get_erp_comparativa(token, {metrica, mes, zona: zona || undefined}, claveActual));
    }, [dispatch, token, metrica, mes, zona, claveActual]);

    useEffect(() => {
        recargar();
    }, [recargar]);

    useEffect(() => {
        if (resultado.status === 201 && resultado.metrica === metrica) {
            recargar();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resultado.status, resultado.metrica]);

    const comparativaVigente = comparativa.clave === claveActual
        ? comparativa : {data: {}, status: 0, message: ""};

    const cargando = pedido && comparativaVigente.status === 0;
    const error = comparativaVigente.status !== 0 && comparativaVigente.status !== 200
        ? (comparativaVigente.message || "No se pudo cargar la comparativa") : null;
    const zonasComparativa = comparativaVigente.data && comparativaVigente.data.datos
        ? comparativaVigente.data.datos : [];

    return (
        <div className="app-content content">
            <div className="content-overlay"/>
            <div className="content-wrapper">
                <div className="content-header row">
                    <div className="content-header-left col-12 mb-2 mt-1">
                        <Breadcrumbs title="ERP" items={[]}/>
                    </div>
                </div>
                <div className="content-body">
                    {puedeCargar && METRICAS.map(([id, etiqueta]) => (
                        <TarjetaCarga key={id} metrica={id} etiqueta={etiqueta} puedeCargar={puedeCargar} token={token}/>
                    ))}

                    <div className="card mb-1">
                        <div className="card-body d-flex flex-wrap align-items-center" style={{gap: "0.5rem"}}>
                            <select
                                className="form-control form-control-sm"
                                style={{maxWidth: "220px"}}
                                value={metrica}
                                onChange={(e) => setMetrica(e.target.value)}
                            >
                                {METRICAS.map(([id, etiqueta]) => (
                                    <option key={id} value={id}>{etiqueta}</option>
                                ))}
                            </select>
                            <input
                                type="month"
                                className="form-control form-control-sm"
                                style={{maxWidth: "160px"}}
                                value={mes}
                                onChange={(e) => setMes(e.target.value)}
                            />
                            <select
                                className="form-control form-control-sm"
                                style={{maxWidth: "220px"}}
                                value={zona}
                                onChange={(e) => setZona(e.target.value)}
                            >
                                <option value="">Todas las zonas</option>
                                {zonas.map((z) => (
                                    <option key={z.id} value={z.id}>{z.nombre}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {cargando && (
                        <div className="alert alert-info" role="status" aria-busy="true">
                            <i className="bx bx-loader-alt bx-spin mr-50"/> Cargando comparativa…
                        </div>
                    )}
                    {error && <div className="alert alert-danger">{error}</div>}
                    {!cargando && !error && zonasComparativa.length === 0 && (
                        <div className="alert alert-info">
                            Carga al menos un Excel de esta métrica para ver la comparativa.
                        </div>
                    )}

                    {zonasComparativa.map((z) => {
                        const colores = NIVEL_COLOR[z.nivel] || {bg: "#f5f5f5", color: "#888"};
                        return (
                            <div className="card mb-1" key={z.id}>
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <b>{z.nombre}</b>
                                        <span>
                                            <span className="text-muted mr-1" style={{fontSize: "12px"}}>
                                                {z.cantidad_tiendas} tienda(s)
                                            </span>
                                            <span style={{
                                                background: colores.bg, color: colores.color,
                                                padding: "2px 8px", borderRadius: "6px", fontWeight: 500,
                                            }}>
                                                {z.promedio !== null ? `${(z.promedio * 100).toFixed(1)}% prom.` : "—"}
                                            </span>
                                        </span>
                                    </div>
                                    <div className="table-responsive">
                                        <table className="table table-hover">
                                            <thead>
                                                <tr>
                                                    <th>Código</th>
                                                    <th>Joyería</th>
                                                    <th className="text-center">IGI</th>
                                                    <th className="text-right">% Cumplimiento</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {z.tiendas.map((t) => {
                                                    const colorIgi = NIVEL_COLOR[nivel3(t.igi)] || {bg: "#f5f5f5", color: "#888"};
                                                    const colorPct = NIVEL_COLOR[t.nivel] || {bg: "#f5f5f5", color: "#888"};
                                                    return (
                                                        <tr key={t.id}>
                                                            <td>{t.codigo}</td>
                                                            <td>{t.nombre}</td>
                                                            <td className="text-center">
                                                                <span style={{
                                                                    background: colorIgi.bg, color: colorIgi.color,
                                                                    padding: "2px 8px", borderRadius: "6px", fontWeight: 500,
                                                                }}>
                                                                    {formatoNota(t.igi)}
                                                                </span>
                                                            </td>
                                                            <td className="text-right">
                                                                <span style={{
                                                                    background: colorPct.bg, color: colorPct.color,
                                                                    padding: "2px 8px", borderRadius: "6px", fontWeight: 500,
                                                                }}>
                                                                    {t.porcentaje !== null
                                                                        ? `${(t.porcentaje * 100).toFixed(1)}%` : "—"}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ERP;
